import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { buildLifeBalanceResult, mergeLatestDomainResults, LB_DOMAINS, type LifeBalanceOverview, type LifeBalanceResult } from '../utils/lifeBalanceScoring';
import {
  buildEnergeticResult,
  mergeLatestDomainResults as mergeEnergeticDomains,
  type EnergeticOverview,
  type EnergeticMap,
} from '../utils/energeticScoring';
import { ENERGETIC_DOMAINS } from '../data/energeticAlignment';
import {
  buildMindResult,
  mergeLatestSubsystemResults,
  type MindOverview,
  type MindMap,
} from '../utils/mindScoring';
import { MIND_SUBSYSTEM_IDS } from '../data/mindAssessment';
import { LIFE_MAP_RESULT_TYPE, type LifeMapSnapshotPayload } from '../constants/lifeMap';
// ⚠️ TYPE-ONLY, deliberately. The service pulls in `aiProxy`; a value import
// here would put a store on that module's runtime graph. `import type` is
// erased at compile time, so this cannot create a cycle.
import type { TalentContext } from '../services/purposeVisionService';
import {
  TALENT_BRIEF_RESULT_TYPE, TALENT_BRIEF_SCOPE, coerceTalentBrief, type TalentBrief,
} from '../constants/talentBrief';
import { reverseLikert } from '../utils/personalityArchetype';
import { buildSpectrumResult, buildShadowMap, quadrantPct, mergeLatestSpectrumResults, type ShadowOverview, type ShadowMap, type QuadrantPcts } from '../utils/shadowScoring';
import { SHADOW_ID, type Quadrant } from '../data/shadowSpectra';
import { ARCHETYPE_ID } from '../data/archetypes';
import { TALENT_ASSESSMENT_ID } from '../constants/assessmentIds';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';
import { buildArchetypeProfile, buildArchetypeMatrixRead, isShadowActive, type DesireCell, type ForcedChoicePick, type CoarseCell } from '../utils/archetypeScoring';

// Upsert a scoped Life Balance result row (one per scope: 'all' or a domain).
async function upsertScopedResult(
  attemptId: string,
  resultType: string,
  scope: string,
  fields: { ai_interpretation?: string; result_scores?: Record<string, any> },
): Promise<boolean> {
  try {
    const { data: existing } = await supabase
      .from('user_assessment_results')
      .select('id')
      .eq('attempt_id', attemptId)
      .eq('result_type', resultType)
      .eq('result_label', scope)
      .maybeSingle();
    if (existing) {
      const { error } = await supabase.from('user_assessment_results').update({ ...fields }).eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from('user_assessment_results').insert({
        attempt_id: attemptId,
        result_type: resultType,
        result_label: scope,
        result_value: null,
        result_scores: fields.result_scores ?? {},
        ai_interpretation: fields.ai_interpretation ?? null,
      });
      if (error) throw error;
    }
    return true;
  } catch {
    return false;
  }
}

export interface Assessment {
  id: string;
  name: string;
  description: string;
  short_description: string | null;
  // ⚠️ Hand-maintained mirror of the `assessments_type_check` constraint — a new
  // type needs BOTH this union and the migration that widens the CHECK, or the
  // row inserts fine and then fails to narrow anywhere in the client.
  type: 'talent' | 'personality' | 'archetype' | 'life_balance' | 'growth' | 'career' | 'values' | 'strengths' | 'job_role' | 'shadow' | 'energetic' | 'mind' | 'identity' | 'life_map';
  methodology: string | null;
  estimated_minutes: number;
  icon: string;
  color: string;
  is_active: boolean;
  created_at: string;
}

export interface AssessmentQuestion {
  id: string;
  assessment_id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'scale' | 'ranking' | 'open_ended' | 'agree_disagree' | 'weighted_choice';
  options: QuestionOption[];
  scoring_key: Record<string, any>;
  dimension: string | null;
  order_index: number;
  is_required: boolean;
}

export interface QuestionOption {
  value: string;
  label: string;
  score?: number;
  dimension?: string;
}

export interface AssessmentAttempt {
  id: string;
  user_id: string;
  assessment_id: string;
  started_at: string;
  completed_at: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  mode?: 'simple' | 'ranked' | 'distribute';
}

export interface AssessmentResponse {
  id: string;
  attempt_id: string;
  question_id: string;
  response_value: string | null;
  response_json: Record<string, any> | null;
  answered_at: string;
}

export interface AssessmentResult {
  id: string;
  attempt_id: string;
  result_type: string;
  result_label: string | null;
  result_value: string | null;
  result_scores: Record<string, any>;
  ai_interpretation: string | null;
  created_at: string;
}

interface AssessmentState {
  assessments: Assessment[];
  currentAssessment: Assessment | null;
  questions: AssessmentQuestion[];
  currentAttempt: AssessmentAttempt | null;
  responses: Record<string, AssessmentResponse>;
  results: AssessmentResult[];
  attemptHistory: AssessmentAttempt[];
  latestResults: Record<string, AssessmentResult[]>;
  /** Life Balance: the whole-life picture merged from per-domain attempts. */
  lbOverview: LifeBalanceOverview;
  /** Life Balance: per-attempt summary for history labels (domain/All + result). */
  lbAttemptSummaries: Record<string, { label: string; status: string; balance: number; avg: number }>;
  /** Shadow Work: the whole map merged from per-spectrum attempts. */
  shadowOverview: ShadowOverview;
  /** Shadow Work: per-attempt summary for history labels (spectrum/All + result). */
  shadowAttemptSummaries: Record<string, { label: string; resultLabel: string; integration: number }>;
  /** Energetic Alignment: the whole-system picture merged from per-domain attempts. */
  eaOverview: EnergeticOverview;
  /** Energetic Alignment: per-attempt summary for history labels. */
  eaAttemptSummaries: Record<string, { label: string; alignmentType: string; alignment: number }>;
  /** Mind: the whole-system picture merged from per-subsystem attempts. */
  mindOverview: MindOverview;
  /** Mind: per-attempt summary for history labels. */
  mindAttemptSummaries: Record<string, { label: string; band: string; strength: number }>;
  isLoading: boolean;
  error: string | null;

  fetchAssessments: () => Promise<void>;
  fetchQuestions: (assessmentId: string) => Promise<void>;
  startAssessment: (assessmentId: string, mode?: 'simple' | 'ranked' | 'distribute') => Promise<string | null>;
  saveResponse: (questionId: string, value: string, jsonData?: Record<string, any>) => Promise<void>;
  completeAssessment: () => Promise<void>;
  calculateResults: (attemptId: string) => Promise<AssessmentResult[]>;
  fetchAttemptHistory: (assessmentId?: string) => Promise<void>;
  /** Rebuild `lbOverview` for the Life Balance assessment from completed attempts. */
  fetchLifeBalanceOverview: (assessmentId: string) => Promise<void>;
  /** Rebuild `shadowOverview` for the Shadow Work assessment from completed attempts. */
  fetchShadowOverview: (assessmentId: string) => Promise<void>;
  /** Fetch + merge the user's latest Shadow Work map (union of each spectrum's
   *  latest take) — the live trait/integration source the Archetype matrix bridges. */
  fetchLatestShadowMap: () => Promise<ShadowMap | null>;
  /** Rebuild `eaOverview` for the Energetic Alignment assessment from completed attempts. */
  fetchEnergeticOverview: (assessmentId: string) => Promise<void>;
  /** Rebuild `mindOverview` for the Mind assessment from completed attempts. */
  fetchMindOverview: (assessmentId: string) => Promise<void>;
  fetchAttemptResults: (attemptId: string) => Promise<AssessmentResult[]>;
  fetchLatestResults: () => Promise<void>;
  /** Delete a single logged attempt (cascades its results + responses), then refresh. */
  deleteAssessmentAttempt: (attemptId: string) => Promise<boolean>;
  /** Delete ALL of an assessment's attempts ("start fresh"), then refresh. XP is kept. */
  deleteAssessmentHistory: (assessmentId: string) => Promise<boolean>;
  /** Internal: refresh caches invalidated by a history deletion. */
  _refreshAfterHistoryDelete: (assessmentId: string) => Promise<void>;
  saveInterpretation: (attemptId: string, text: string) => Promise<boolean>;
  /** Persist the Personality "Type" lens AI read as its own result row (no migration). */
  savePersonalityTypeInterpretation: (attemptId: string, text: string) => Promise<boolean>;
  /** Persist a generated action plan as an `action_plan` result row (no migration). */
  saveActionPlan: (attemptId: string, plan: Record<string, any>) => Promise<boolean>;
  /** Persist the inline "quick read" coarse cells onto the latest Archetype attempt
   *  (no XP — not a scored assessment). Returns that attempt id, or null if none. */
  saveCoarseShadow: (cells: CoarseCell[]) => Promise<string | null>;
  /**
   * Record a completed attempt for a flow that has NO questions and no scoring.
   *
   * ⚠️ The North-Star Identity is a guided reflection, so it never touches
   * startAssessment/completeAssessment — which is exactly why nothing showed up
   * in History. This writes the attempt and its result snapshot in one go, so a
   * finished sitting is reviewable like every other assessment.
   */
  recordReflectionAttempt: (input: {
    assessmentId: string;
    resultType: string;
    label: string;
    payload: Record<string, any>;
  }) => Promise<string | null>;
  /** Persist the structured half of a Talent report — read by the Direction prompts. */
  saveTalentBrief: (attemptId: string, brief: TalentBrief) => Promise<boolean>;
  /** Life Balance: persist AI interpretation/action-plan per scope ('all' or a domain). */
  saveScopedInterpretation: (attemptId: string, scope: string, text: string) => Promise<boolean>;
  saveScopedActionPlan: (attemptId: string, scope: string, plan: Record<string, any>) => Promise<boolean>;
  /**
   * ⚠️ The type lives in `services/purposeVisionService` — the two AI services
   * that consume it already share it from there, and a second declaration here
   * is how the store starts returning a field no prompt reads.
   */
  getTalentContext: () => TalentContext;
  abandonAssessment: () => Promise<void>;
  setCurrentAssessment: (assessment: Assessment | null) => void;
  clearCurrentAttempt: () => void;
  getCompletedAssessments: () => Assessment[];
  /**
   * The latest saved result of a REFLECTION — North-Star, the identity gap, the
   * Life Map — read by its own `result_type`.
   *
   * ⚠️ Reads `latestResults`, NOT `results`. `results` holds only the attempt
   * being taken right now and is emptied by `clearCurrentAttempt`, so a reader
   * that used it would find a past sitting only in the seconds after saving it.
   * ⚠️ Requires `fetchLatestResults()` to have run — the Assessment and Profile
   * tabs both do so on mount, and `recordReflectionAttempt` refreshes it. It is
   * NOT persisted, so do not read it from a surface that never fetches; derive
   * "has completed this" from `attemptHistory` instead, which is.
   */
  getLatestReflectionResult: (assessmentId: string, resultType: string) => AssessmentResult | null;
  /**
   * ⭐ Every Life Map sitting's saved payload, by attempt id (owner, 2026-10-10):
   * History labels each check-in with its level, and opens it read-only.
   */
  lifeMapSnapshots: Record<string, LifeMapSnapshotPayload>;
  /** Load the payloads of these attempts (one query; already-loaded ones are skipped). */
  fetchLifeMapSnapshots: (attemptIds: string[]) => Promise<void>;
}

export const useAssessmentStore = create<AssessmentState>()(
  persist(
    (set, get) => ({
      assessments: [],
      currentAssessment: null,
      questions: [],
      currentAttempt: null,
      responses: {},
      results: [],
      attemptHistory: [],
      latestResults: {},
      lifeMapSnapshots: {},
      lbOverview: { result: null, perDomain: {} },
      lbAttemptSummaries: {},
      eaOverview: { result: null, perDomain: {} },
      eaAttemptSummaries: {},
      mindOverview: { result: null, perSubsystem: {} },
      mindAttemptSummaries: {},
      shadowOverview: { result: null, perSpectrum: {} },
      shadowAttemptSummaries: {},
      isLoading: false,
      error: null,

      fetchAssessments: async () => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await supabase
            .from('assessments')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: true });

          if (error) throw error;
          set({ assessments: data || [], isLoading: false });
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      fetchQuestions: async (assessmentId: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await supabase
            .from('assessment_questions')
            .select('*')
            .eq('assessment_id', assessmentId)
            .order('order_index', { ascending: true });

          if (error) throw error;

          // Life Balance: order questions by the canonical domain order (so the full
          // assessment + its progress cards walk Personal…Ecological…Existential to
          // match the radar/picker), then by order_index within each domain.
          let questions = data || [];
          const assessment = get().assessments.find(a => a.id === assessmentId);
          if (assessment?.type === 'life_balance') {
            const order = LB_DOMAINS as readonly string[];
            questions = [...questions].sort((a, b) => {
              const da = order.indexOf(a.dimension ?? '');
              const db = order.indexOf(b.dimension ?? '');
              if (da !== db) return (da === -1 ? 99 : da) - (db === -1 ? 99 : db);
              return (a.order_index ?? 0) - (b.order_index ?? 0);
            });
          }
          set({ questions, isLoading: false });
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      startAssessment: async (assessmentId: string, mode: 'simple' | 'ranked' | 'distribute' = 'simple') => {
        set({ isLoading: true, error: null });
        // ⚠️ Started as the account signs out: its new attempt is never the next account's to answer.
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          const { data, error } = await supabase
            .from('user_assessment_attempts')
            .insert({
              user_id: user.id,
              assessment_id: assessmentId,
              status: 'in_progress',
              mode
            })
            .select()
            .single();

          if (at !== accountGeneration()) return null;
          if (error) throw error;

          const assessment = get().assessments.find(a => a.id === assessmentId);
          set({
            currentAttempt: data,
            currentAssessment: assessment || null,
            responses: {},
            isLoading: false
          });

          await get().fetchQuestions(assessmentId);
          return data.id;
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
          return null;
        }
      },

      saveResponse: async (questionId: string, value: string, jsonData?: Record<string, any>) => {
        const { currentAttempt } = get();
        if (!currentAttempt) return;

        try {
          const { data, error } = await supabase
            .from('user_assessment_responses')
            .upsert({
              attempt_id: currentAttempt.id,
              question_id: questionId,
              response_value: value,
              response_json: jsonData || null,
              answered_at: new Date().toISOString()
            }, {
              onConflict: 'attempt_id,question_id'
            })
            .select()
            .single();

          if (error) throw error;

          set(state => ({
            responses: {
              ...state.responses,
              [questionId]: data
            }
          }));
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      completeAssessment: async () => {
        const { currentAttempt } = get();
        if (!currentAttempt) return;
        const at = accountGeneration();

        set({ isLoading: true });
        try {
          const { error } = await supabase
            .from('user_assessment_attempts')
            .update({
              status: 'completed',
              completed_at: new Date().toISOString()
            })
            .eq('id', currentAttempt.id);

          if (error) throw error;

          const results = await get().calculateResults(currentAttempt.id);
          /* ⚠️ Finished as the account signs out: its scores are the last account's — never
             set as the next one's results, nor the reason to reload its caches. */
          if (at !== accountGeneration()) return;

          set(state => ({
            currentAttempt: state.currentAttempt ? {
              ...state.currentAttempt,
              status: 'completed',
              completed_at: new Date().toISOString()
            } : null,
            results,
            isLoading: false
          }));

          // Refresh history + latest-results caches so the just-completed attempt
          // (and its "View results" affordance / completion check) appears
          // immediately on the detail page — without waiting for a remount.
          await Promise.all([get().fetchAttemptHistory(), get().fetchLatestResults()]);

          // Life Balance: rebuild the merged whole-life overview so the front
          // page reflects the domain that was just (re)assessed.
          const assessment = get().assessments.find(a => a.id === currentAttempt.assessment_id);
          if (assessment?.type === 'life_balance') {
            await get().fetchLifeBalanceOverview(assessment.id);
          }
          // Shadow Work: rebuild the merged whole-map overview after a spectrum.
          if (assessment?.id === SHADOW_ID) {
            await get().fetchShadowOverview(assessment.id);
          }
          // Energetic Alignment: rebuild the merged whole-system overview after a domain.
          if (assessment?.type === 'energetic') {
            await get().fetchEnergeticOverview(assessment.id);
          }
          // Mind: same — rebuild the merged whole-mind overview after a subsystem.
          if (assessment?.type === 'mind') {
            await get().fetchMindOverview(assessment.id);
          }
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      calculateResults: async (attemptId: string) => {
        const { questions, responses, currentAssessment, currentAttempt } = get();
        if (!currentAssessment) return [];

        // Life Balance is bidirectional ("just-right"): 5 = ideal, distance = imbalance.
        // The generic "higher = better" averaging below is wrong here, so we score it with
        // the custom util and persist a `balance_matrix` row (full payload) + reuse
        // `dimension_scores` (per-domain rawAvg) so existing domain-radar consumers still work.
        if (currentAssessment.type === 'life_balance') {
          const cells = questions
            .map(question => {
              const response = responses[question.id];
              if (!response) return null;
              const parameter = question.scoring_key?.parameter;
              const answer = parseInt(response.response_value || '', 10);
              if (!question.dimension || !parameter || !Number.isFinite(answer)) return null;
              return { domain: question.dimension, parameter: String(parameter), answer };
            })
            .filter((c): c is { domain: string; parameter: string; answer: number } => c !== null);

          const payload = buildLifeBalanceResult(cells);

          // Per-domain raw averages keep the generic domain-radar consumers working.
          const dimensionScores: Record<string, number> = {};
          for (const [domain, agg] of Object.entries(payload.domains)) {
            dimensionScores[domain] = agg.rawAvg;
          }

          const lbResults: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
            {
              attempt_id: attemptId,
              result_type: 'balance_matrix',
              result_label: 'Life Balance',
              result_value: String(payload.overall.balance),
              result_scores: payload,
              ai_interpretation: null
            },
            {
              attempt_id: attemptId,
              result_type: 'dimension_scores',
              result_label: 'Domain Balance',
              result_value: null,
              result_scores: dimensionScores,
              ai_interpretation: null
            },
            {
              attempt_id: attemptId,
              result_type: 'primary_type',
              result_label: payload.overall.status,
              result_value: String(payload.overall.balance),
              result_scores: {},
              ai_interpretation: null
            }
          ];

          const insertedLb: AssessmentResult[] = [];
          for (const result of lbResults) {
            const { data, error } = await supabase
              .from('user_assessment_results')
              .insert(result)
              .select()
              .single();
            if (!error && data) insertedLb.push(data);
          }
          return insertedLb;
        }

        // Energetic Alignment (type 'energetic') — twin of Life Balance, but a 1–7
        // scale (ideal 4) scored for centricity + SD-scatter → Alignment + the 2×2
        // alignment type. Persist an `energetic_map` row (full payload) + per-domain
        // centricity in `dimension_scores` + the overall type in `primary_type`.
        if (currentAssessment.type === 'energetic') {
          const cells = questions
            .map(question => {
              const response = responses[question.id];
              if (!response) return null;
              const parameter = question.scoring_key?.parameter;
              const answer = parseInt(response.response_value || '', 10);
              if (!question.dimension || !parameter || !Number.isFinite(answer)) return null;
              return { domainId: question.dimension, parameter: String(parameter), answer };
            })
            .filter((c): c is { domainId: string; parameter: string; answer: number } => c !== null);

          const payload = buildEnergeticResult(cells);

          const dimensionScores: Record<string, number> = {};
          for (const d of payload.domains) dimensionScores[d.domainId] = d.centricity;

          const eaResults: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
            {
              attempt_id: attemptId,
              result_type: 'energetic_map',
              result_label: 'Energetic Alignment',
              result_value: String(payload.overall.overallAlignment),
              result_scores: payload,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'dimension_scores',
              result_label: 'Domain Centricity',
              result_value: null,
              result_scores: dimensionScores,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'primary_type',
              result_label: payload.overall.overallAlignmentType,
              result_value: String(payload.overall.overallAlignment),
              result_scores: {},
              ai_interpretation: null,
            },
          ];

          const insertedEa: AssessmentResult[] = [];
          for (const result of eaResults) {
            const { data, error } = await supabase
              .from('user_assessment_results')
              .insert(result)
              .select()
              .single();
            if (!error && data) insertedEa.push(data);
          }
          return insertedEa;
        }

        // Mind (type 'mind') — 7×7 like Energetic, but MONOTONIC: higher is simply
        // better, so the score is the plain mean (Strength) and there is no ideal.
        // Persist a `mind_map` row (full payload) + per-subsystem strength in
        // `dimension_scores` + the overall band in `primary_type`.
        //
        // ⚠️ `question.dimension` is the subsystem ID here ('clarity'), not its
        // display name — Energetic keys by name, but Mind's names carry spaces and
        // ampersands. Keep this aligned with migration 0210 and MIND_SUBSYSTEM_IDS.
        if (currentAssessment.type === 'mind') {
          const cells = questions
            .map(question => {
              const response = responses[question.id];
              if (!response) return null;
              const parameter = question.scoring_key?.parameter;
              const answer = parseInt(response.response_value || '', 10);
              if (!question.dimension || !parameter || !Number.isFinite(answer)) return null;
              return { subsystemId: question.dimension, parameter: String(parameter), answer };
            })
            .filter((c): c is { subsystemId: string; parameter: string; answer: number } => c !== null);

          const payload = buildMindResult(cells);

          const dimensionScores: Record<string, number> = {};
          for (const s of payload.subsystems) dimensionScores[s.subsystemId] = s.strength;

          const mindResults: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
            {
              attempt_id: attemptId,
              result_type: 'mind_map',
              result_label: 'Mind',
              result_value: String(payload.overall.overallStrength),
              result_scores: payload,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'dimension_scores',
              result_label: 'Subsystem Strength',
              result_value: null,
              result_scores: dimensionScores,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'primary_type',
              result_label: payload.overall.overallBand,
              result_value: String(payload.overall.overallStrength),
              result_scores: {},
              ai_interpretation: null,
            },
          ];

          const insertedMind: AssessmentResult[] = [];
          for (const result of mindResults) {
            const { data, error } = await supabase
              .from('user_assessment_results')
              .insert(result)
              .select()
              .single();
            if (!error && data) insertedMind.push(data);
          }
          return insertedMind;
        }

        // Shadow Work is a two-axis instrument (dimension = spectrum, scoring_key =
        // {quadrant}). Group answered items by spectrum → quadrant, average each
        // quadrant to a %, then run the custom shadow scorer. A spectrum is scored
        // only if all 4 quadrants were answered (so a modular per-spectrum attempt
        // yields exactly that spectrum). Branch on the assessment ID (type 'shadow').
        if (currentAssessment.id === SHADOW_ID) {
          const grouped: Record<string, Record<Quadrant, number[]>> = {};
          for (const question of questions) {
            const response = responses[question.id];
            if (!response) continue;
            const spectrumId = question.dimension;
            const quadrant = question.scoring_key?.quadrant as Quadrant | undefined;
            const val = parseInt(response.response_value || '', 10);
            if (!spectrumId || !quadrant || !Number.isFinite(val)) continue;
            (grouped[spectrumId] ||= { LP: [], LS: [], RP: [], RS: [] });
            grouped[spectrumId][quadrant].push(val);
          }

          const spectra = Object.entries(grouped)
            .filter(([, q]) => q.LP.length && q.LS.length && q.RP.length && q.RS.length)
            .map(([spectrumId, q]) => {
              const quads: QuadrantPcts = {
                LP: quadrantPct(q.LP),
                LS: quadrantPct(q.LS),
                RP: quadrantPct(q.RP),
                RS: quadrantPct(q.RS),
              };
              return buildSpectrumResult(spectrumId, quads);
            });

          const payload = buildShadowMap(spectra);

          // Integration per spectrum keeps the radar (dimension_scores) consumers working.
          const dimensionScores: Record<string, number> = {};
          for (const s of payload.spectra) dimensionScores[s.spectrumId] = s.integrationScore;

          const shadowResults: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
            {
              attempt_id: attemptId,
              result_type: 'shadow_map',
              result_label: 'Shadow Work',
              result_value: String(payload.overall.integration),
              result_scores: payload,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'dimension_scores',
              result_label: 'Spectrum Integration',
              result_value: null,
              result_scores: dimensionScores,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'primary_type',
              result_label: payload.overall.status,
              result_value: String(payload.overall.integration),
              result_scores: {},
              ai_interpretation: null,
            },
          ];

          const insertedShadow: AssessmentResult[] = [];
          for (const result of shadowResults) {
            const { data, error } = await supabase
              .from('user_assessment_results')
              .insert(result)
              .select()
              .single();
            if (!error && data) insertedShadow.push(data);
          }
          return insertedShadow;
        }

        // Archetype Matrix (…015) — the capstone. Score the Core Desire layer here;
        // BRIDGE the trait/integration layer from the user's latest Shadow Work
        // (…013) result to position the dominant archetype's 10×5 matrix (plan §6).
        if (currentAssessment.id === ARCHETYPE_ID) {
          const desireCells: DesireCell[] = [];
          const fcPicks: ForcedChoicePick[] = [];
          for (const question of questions) {
            const response = responses[question.id];
            if (!response) continue;
            if (question.question_type === 'scale' && question.dimension && question.dimension !== 'forced_choice') {
              const value = parseInt(response.response_value || '', 10);
              if (Number.isFinite(value)) desireCells.push({ archetypeId: question.dimension, value });
            } else if (question.question_type === 'multiple_choice' && question.dimension === 'forced_choice') {
              const picked = response.response_value || '';
              const archetypeIds = picked.split('+').map(s => s.trim()).filter(Boolean);
              if (archetypeIds.length) fcPicks.push({ archetypeIds });
            }
          }

          const profile = buildArchetypeProfile(desireCells, fcPicks);

          // Snapshot the user's latest completed Shadow Work map (the trait/
          // integration source) so the matrix has an immediate value. The results
          // view re-reads this live at VIEW time (fetchLatestShadowMap), so the
          // matrix fills in whenever Shadow Work is done — regardless of order.
          const shadowMap = await get().fetchLatestShadowMap();

          const matrix = buildArchetypeMatrixRead(profile.dominant.id, shadowMap);
          const payload = {
            profile,
            dominantId: profile.dominant.id,
            matrix,
            hasShadowData: matrix.hasShadowData,
            shadowActive: isShadowActive(matrix),
          };

          const dimensionScores: Record<string, number> = {};
          for (const s of profile.scores) dimensionScores[s.id] = s.score;

          const archResults: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
            {
              attempt_id: attemptId,
              result_type: 'archetype_profile',
              result_label: 'Archetype Matrix',
              result_value: String(profile.dominant.score),
              result_scores: payload,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'dimension_scores',
              result_label: 'Desire Scores',
              result_value: null,
              result_scores: dimensionScores,
              ai_interpretation: null,
            },
            {
              attempt_id: attemptId,
              result_type: 'primary_type',
              result_label: profile.dominant.lightName,
              result_value: String(profile.dominant.score),
              result_scores: {},
              ai_interpretation: null,
            },
          ];

          const insertedArch: AssessmentResult[] = [];
          for (const result of archResults) {
            const { data, error } = await supabase
              .from('user_assessment_results')
              .insert(result)
              .select()
              .single();
            if (!error && data) insertedArch.push(data);
          }
          return insertedArch;
        }

        // The attempt's chosen mode decides how talent questions are scored:
        // simple = single pick, ranked = top-3 (3/2/1), distribute = 12 points spread.
        const mode: 'simple' | 'ranked' | 'distribute' = currentAttempt?.mode || 'simple';

        // `mode` decides how a dimension's raw scores are normalized:
        //  - 'avg' (scale / agree_disagree, and legacy multiple_choice keyed off
        //    question.dimension): average response value for the dimension.
        //  - 'sum' (per-option-dimension multiple_choice, e.g. the Talent model
        //    where each picked option tallies +score toward its own talent):
        //    the running total IS the score (picked 5× > picked 1×).
        const dimensionScores: Record<string, { total: number; count: number; mode: 'sum' | 'avg' }> = {};

        const addScore = (dimension: string | null | undefined, score: number, mode: 'sum' | 'avg') => {
          if (!dimension) return;
          if (!dimensionScores[dimension]) {
            dimensionScores[dimension] = { total: 0, count: 0, mode };
          }
          dimensionScores[dimension].total += score;
          dimensionScores[dimension].count += 1;
        };

        questions.forEach(question => {
          const response = responses[question.id];
          if (!response) return;

          if (question.question_type === 'scale' || question.question_type === 'agree_disagree') {
            if (!question.dimension) return;
            let score = parseInt(response.response_value || '0', 10);
            // Reverse-scored items (e.g. Personality's R statements) flip on their
            // own scale so a "strongly agree" lowers the trait. agree_disagree is a
            // fixed 5-point scale; scale assessments size off their option count.
            if (question.scoring_key?.reverse && Number.isFinite(score)) {
              const points = question.question_type === 'agree_disagree' ? 5 : (question.options?.length || 5);
              score = reverseLikert(score, points);
            }
            addScore(question.dimension, score, 'avg');
            return;
          }

          // Talent questions (multiple_choice; legacy weighted_choice) are scored by
          // the attempt's mode — each picked option adds points to its own talent.
          if (question.question_type === 'multiple_choice' || question.question_type === 'weighted_choice') {
            if (mode === 'distribute') {
              const rj = response.response_json as { distribution?: Record<string, number> } | null;
              const dist = rj?.distribution || {};
              Object.entries(dist).forEach(([value, points]) => {
                const option = question.options.find(opt => opt.value === value);
                if (option?.dimension) addScore(option.dimension, Number(points) || 0, 'sum');
              });
            } else if (mode === 'ranked') {
              const rj = response.response_json as { picks?: Array<{ value: string; points?: number }> } | null;
              const picks = rj && Array.isArray(rj.picks) ? rj.picks : [];
              picks.forEach(pick => {
                const option = question.options.find(opt => opt.value === pick.value);
                if (option?.dimension) addScore(option.dimension, pick.points ?? 0, 'sum');
              });
            } else {
              const option = question.options.find(opt => opt.value === response.response_value);
              if (option?.dimension) {
                addScore(option.dimension, option.score ?? 1, 'sum');
              } else if (question.dimension && option?.score) {
                addScore(question.dimension, option.score, 'avg');
              }
            }
          }
        });

        // Sum-mode dimensions (talent) are normalized to a % share of the total so
        // the three input modes are directly comparable; avg-mode dimensions
        // (scale / agree_disagree assessments) keep their averaged value.
        const sumTotal = Object.values(dimensionScores)
          .filter(d => d.mode === 'sum')
          .reduce((s, d) => s + d.total, 0);

        const normalizedScores: Record<string, number> = {};
        Object.entries(dimensionScores).forEach(([dimension, d]) => {
          if (d.mode === 'sum') {
            normalizedScores[dimension] = sumTotal > 0 ? Math.round((d.total / sumTotal) * 1000) / 10 : 0;
          } else {
            normalizedScores[dimension] = d.count > 0 ? Math.round((d.total / d.count) * 10) / 10 : 0;
          }
        });

        const sortedDimensions = Object.entries(normalizedScores)
          .sort(([, a], [, b]) => b - a);

        const primaryResult = sortedDimensions[0];
        const resultLabel = primaryResult ? primaryResult[0] : 'Unknown';
        const resultValue = primaryResult ? String(primaryResult[1]) : '0';

        const results: Omit<AssessmentResult, 'id' | 'created_at'>[] = [
          {
            attempt_id: attemptId,
            result_type: 'dimension_scores',
            result_label: 'Score Breakdown',
            result_value: null,
            result_scores: normalizedScores,
            ai_interpretation: null
          },
          {
            attempt_id: attemptId,
            result_type: 'primary_type',
            result_label: resultLabel,
            result_value: resultValue,
            result_scores: {},
            ai_interpretation: null
          }
        ];

        const insertedResults: AssessmentResult[] = [];
        for (const result of results) {
          const { data, error } = await supabase
            .from('user_assessment_results')
            .insert(result)
            .select()
            .single();

          if (!error && data) {
            insertedResults.push(data);
          }
        }

        return insertedResults;
      },

      /**
       * ⭐ Persist the machine-readable half of a Talent report — see
       * `constants/talentBrief.ts` for why the Direction prompt reads this
       * instead of the prose.
       *
       * ⚠️ Its own row rather than a second column on `primary_type`: the brief
       * is structured and the column it would share (`ai_interpretation`) is
       * text that the results page renders directly. `user_assessment_results`
       * already takes an arbitrary `result_type` with a jsonb payload, so this
       * needed NO migration.
       *
       * ⚠️ Patches `latestResults` as well as `results`, because
       * `getTalentContext` reads the former — without that the brief would not
       * reach the Purpose prompt until the next full fetch, which is exactly
       * the "I saved it and nothing changed" report this whole thread began as.
       */
      saveTalentBrief: async (attemptId: string, brief: TalentBrief) => {
        const ok = await upsertScopedResult(
          attemptId,
          TALENT_BRIEF_RESULT_TYPE,
          TALENT_BRIEF_SCOPE,
          { result_scores: brief as unknown as AssessmentResult['result_scores'] },
        );
        if (!ok) return false;

        set(state => {
          const stamp = (rows: AssessmentResult[]): AssessmentResult[] => {
            const idx = rows.findIndex(
              r => r.attempt_id === attemptId && r.result_type === TALENT_BRIEF_RESULT_TYPE,
            );
            const row: AssessmentResult = {
              id: idx >= 0 ? rows[idx].id : `local-${TALENT_BRIEF_RESULT_TYPE}-${attemptId}`,
              attempt_id: attemptId,
              result_type: TALENT_BRIEF_RESULT_TYPE,
              result_label: TALENT_BRIEF_SCOPE,
              result_value: null,
              result_scores: brief as unknown as AssessmentResult['result_scores'],
              ai_interpretation: null,
              created_at: idx >= 0 ? rows[idx].created_at : new Date().toISOString(),
            };
            if (idx >= 0) {
              const next = rows.slice();
              next[idx] = row;
              return next;
            }
            // ⚠️ Only ever appended to a list that already belongs to this
            // attempt — otherwise a brief would be grafted onto the cached rows
            // of an unrelated assessment.
            return rows.some(r => r.attempt_id === attemptId) ? [...rows, row] : rows;
          };

          const latestResults: Record<string, AssessmentResult[]> = {};
          for (const [aid, rows] of Object.entries(state.latestResults)) latestResults[aid] = stamp(rows);
          return { results: stamp(state.results), latestResults };
        });
        return true;
      },

      // Persist an AI interpretation onto the attempt's primary_type result row so
      // it can be viewed later (and consumed by Purpose & Vision) without re-calling
      // the AI. Also patches the in-memory results + latestResults caches.
      saveInterpretation: async (attemptId: string, text: string) => {
        try {
          const { error } = await supabase
            .from('user_assessment_results')
            .update({ ai_interpretation: text })
            .eq('attempt_id', attemptId)
            .eq('result_type', 'primary_type');
          if (error) throw error;

          set(state => {
            const patch = (r: AssessmentResult) =>
              r.attempt_id === attemptId && r.result_type === 'primary_type'
                ? { ...r, ai_interpretation: text }
                : r;
            const latestResults: Record<string, AssessmentResult[]> = {};
            for (const [aid, rows] of Object.entries(state.latestResults)) {
              latestResults[aid] = rows.map(patch);
            }
            return { results: state.results.map(patch), latestResults };
          });
          return true;
        } catch (error: any) {
          set({ error: error.message });
          return false;
        }
      },

      // The Personality "Type" lens gets its OWN AI read, kept separate from the
      // archetype interpretation (primary_type). Persist it as a `type_interpretation`
      // row (ai_interpretation = text). Upsert — no delete policy on the table.
      savePersonalityTypeInterpretation: async (attemptId: string, text: string) => {
        try {
          const { data: existing } = await supabase
            .from('user_assessment_results')
            .select('id')
            .eq('attempt_id', attemptId)
            .eq('result_type', 'type_interpretation')
            .maybeSingle();

          if (existing) {
            const { error } = await supabase
              .from('user_assessment_results')
              .update({ ai_interpretation: text })
              .eq('id', existing.id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('user_assessment_results')
              .insert({
                attempt_id: attemptId,
                result_type: 'type_interpretation',
                result_label: 'Type read',
                result_value: null,
                result_scores: {},
                ai_interpretation: text,
              });
            if (error) throw error;
          }
          return true;
        } catch (error: any) {
          set({ error: error.message });
          return false;
        }
      },

      // Persist a generated action plan as its own result row (result_type
      // 'action_plan', plan in result_scores) so it survives navigation/reload
      // without a schema migration. Patches the in-memory caches too.
      saveActionPlan: async (attemptId: string, plan: Record<string, any>) => {
        const at = accountGeneration();
        try {
          const { data: existing } = await supabase
            .from('user_assessment_results')
            .select('id')
            .eq('attempt_id', attemptId)
            .eq('result_type', 'action_plan')
            .maybeSingle();

          if (existing) {
            const { error } = await supabase
              .from('user_assessment_results')
              .update({ result_scores: plan })
              .eq('id', existing.id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('user_assessment_results')
              .insert({
                attempt_id: attemptId,
                result_type: 'action_plan',
                result_label: 'Action Plan',
                result_value: null,
                result_scores: plan,
                ai_interpretation: null,
              });
            if (error) throw error;
          }
          /* ⚠️⚠️ The plan is added to EVERY cached list, whoever's — saved as the account
             signs out, it would sit in the next account's results. Saved for the one that
             left; nothing of it in the next one's. */
          if (at !== accountGeneration()) return true;

          set(state => {
            const upsert = (rows: AssessmentResult[]): AssessmentResult[] => {
              const others = rows.filter(r => !(r.attempt_id === attemptId && r.result_type === 'action_plan'));
              return [
                ...others,
                {
                  id: existing?.id ?? `local-action-plan-${attemptId}`,
                  attempt_id: attemptId,
                  result_type: 'action_plan',
                  result_label: 'Action Plan',
                  result_value: null,
                  result_scores: plan,
                  ai_interpretation: null,
                  created_at: '',
                },
              ];
            };
            const latestResults: Record<string, AssessmentResult[]> = {};
            for (const [aid, rows] of Object.entries(state.latestResults)) latestResults[aid] = upsert(rows);
            return { results: upsert(state.results), latestResults };
          });
          return true;
        } catch (error: any) {
          set({ error: error.message });
          return false;
        }
      },

      // The inline "quick read": persist coarse per-spectrum cells onto the user's
      // latest completed Archetype attempt so its matrix can be positioned without a
      // full Shadow Work result. Not a scored assessment → mints NO XP; a real Shadow
      // Work result still overrides it at view time (AssessmentResults precedence).
      // Upsert (no delete policy on the table): update an existing row, else insert.
      saveCoarseShadow: async (cells: CoarseCell[]) => {
        try {
          const latest = get().attemptHistory
            .filter(a => a.assessment_id === ARCHETYPE_ID && a.status === 'completed')
            .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())[0];
          if (!latest) return null;

          const { data: existing } = await supabase
            .from('user_assessment_results')
            .select('id')
            .eq('attempt_id', latest.id)
            .eq('result_type', 'coarse_shadow')
            .maybeSingle();

          if (existing) {
            const { error } = await supabase
              .from('user_assessment_results')
              .update({ result_scores: { cells } })
              .eq('id', existing.id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('user_assessment_results')
              .insert({
                attempt_id: latest.id,
                result_type: 'coarse_shadow',
                result_label: 'Quick read',
                result_value: null,
                result_scores: { cells },
                ai_interpretation: null,
              });
            if (error) throw error;
          }
          return latest.id;
        } catch (error: any) {
          set({ error: error.message });
          return null;
        }
      },

      recordReflectionAttempt: async ({ assessmentId, resultType, label, payload }) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          const now = new Date().toISOString();
          // Inserted already-completed: there is no in-progress phase to model.
          // A reflection is finished at the moment the user approves it, and an
          // 'in_progress' row would linger forever if they walked away.
          const { data: attempt, error: attemptError } = await supabase
            .from('user_assessment_attempts')
            .insert({
              user_id: user.id,
              assessment_id: assessmentId,
              status: 'completed',
              mode: 'simple',
              started_at: now,
              completed_at: now,
            })
            .select()
            .single();
          if (attemptError) throw attemptError;

          const { error: resultError } = await supabase
            .from('user_assessment_results')
            .insert({
              attempt_id: attempt.id,
              result_type: resultType,
              result_label: label,
              result_value: null,
              result_scores: payload,
              ai_interpretation: null,
            });
          if (resultError) throw resultError;

          // Refresh both caches so the row appears in History and the library's
          // completed tick without a reload.
          await get().fetchAttemptHistory();
          await get().fetchLatestResults();
          return attempt.id;
        } catch (error: any) {
          set({ error: error.message });
          return null;
        }
      },

      // Life Balance scoped AI: persist an interpretation/action-plan for a specific
      // scope ('all' or a domain name) as its own result row on the results attempt,
      // so each domain (and the whole-life view) keeps its own AI across reloads.
      saveScopedInterpretation: async (attemptId: string, scope: string, text: string) => {
        return upsertScopedResult(attemptId, 'lb_interpretation', scope, { ai_interpretation: text });
      },
      saveScopedActionPlan: async (attemptId: string, scope: string, plan: Record<string, any>) => {
        return upsertScopedResult(attemptId, 'lb_action_plan', scope, { result_scores: plan });
      },

      /* ⚠️⚠️ PER ACCOUNT (lib/accountStorage). `attemptHistory` is saved, and Home, the
         Purpose wizard and the profile read the saved copy without loading it — it was one
         key for the device, so the next account to sign in "had taken" the last one's
         assessments. Every load below drops an answer that lands after a change of account. */
      fetchAttemptHistory: async (assessmentId?: string) => {
        set({ isLoading: true });
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          let query = supabase
            .from('user_assessment_attempts')
            .select('*')
            .eq('user_id', user.id)
            .order('started_at', { ascending: false });

          if (assessmentId) {
            query = query.eq('assessment_id', assessmentId);
          }

          const { data, error } = await query;
          if (at !== accountGeneration()) return;
          if (error) throw error;

          set({ attemptHistory: data || [], isLoading: false });
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      fetchAttemptResults: async (attemptId: string) => {
        try {
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('*')
            .eq('attempt_id', attemptId);

          if (error) throw error;
          return data || [];
        } catch (error: any) {
          set({ error: error.message });
          return [];
        }
      },

      // Life Balance overview: the modular model takes each domain as its own
      // attempt, so the whole-life picture is the union of each domain's LATEST
      // take. Batch-fetch the `balance_matrix` rows for completed attempts and
      // merge them (newest-per-domain wins) into one 7×7 payload + per-domain meta.
      fetchLifeBalanceOverview: async (assessmentId: string) => {
        const at = accountGeneration();
        try {
          const completed = get().attemptHistory.filter(
            a => a.assessment_id === assessmentId && a.status === 'completed',
          );
          if (!completed.length) {
            set({ lbOverview: { result: null, perDomain: {} }, lbAttemptSummaries: {} });
            return;
          }
          const ids = completed.map(a => a.id);
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', ids)
            .eq('result_type', 'balance_matrix');
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const byAttempt = new Map<string, LifeBalanceResult>(
            (data || []).map(r => [r.attempt_id as string, r.result_scores as LifeBalanceResult]),
          );
          const attempts = completed.map(a => ({
            completedAt: a.completed_at || a.started_at,
            result: byAttempt.get(a.id),
          }));

          // Per-attempt summary for history labels: a single-domain attempt shows
          // that domain's name; a full pass shows "All".
          const summaries: Record<string, { label: string; status: string; balance: number; avg: number }> = {};
          for (const a of completed) {
            const payload = byAttempt.get(a.id);
            if (!payload) continue;
            const domainKeys = Object.keys(payload.domains);
            summaries[a.id] = {
              label: domainKeys.length === 1 ? domainKeys[0] : 'All domains',
              status: payload.overall.status,
              balance: payload.overall.balance,
              avg: payload.overall.rawAvg,
            };
          }
          set({ lbOverview: mergeLatestDomainResults(attempts), lbAttemptSummaries: summaries });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      // Energetic Alignment: same modular-merge pattern as Life Balance — fetch the
      // `energetic_map` rows for completed attempts and merge newest-per-domain.
      fetchEnergeticOverview: async (assessmentId: string) => {
        const at = accountGeneration();
        try {
          const completed = get().attemptHistory.filter(
            a => a.assessment_id === assessmentId && a.status === 'completed',
          );
          if (!completed.length) {
            set({ eaOverview: { result: null, perDomain: {} }, eaAttemptSummaries: {} });
            return;
          }
          const ids = completed.map(a => a.id);
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', ids)
            .eq('result_type', 'energetic_map');
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const byAttempt = new Map<string, EnergeticMap>(
            (data || []).map(r => [r.attempt_id as string, r.result_scores as EnergeticMap]),
          );
          const attempts = completed.map(a => ({
            completedAt: a.completed_at || a.started_at,
            result: byAttempt.get(a.id),
          }));

          const summaries: Record<string, { label: string; alignmentType: string; alignment: number }> = {};
          for (const a of completed) {
            const payload = byAttempt.get(a.id);
            if (!payload) continue;
            const domainKeys = payload.domains.map(d => d.domainId);
            summaries[a.id] = {
              label: domainKeys.length === 1 ? domainKeys[0] : 'All domains',
              alignmentType: payload.overall.overallAlignmentType,
              alignment: payload.overall.overallAlignment,
            };
          }
          set({
            eaOverview: mergeEnergeticDomains(attempts, ENERGETIC_DOMAINS.map(d => d.name)),
            eaAttemptSummaries: summaries,
          });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      // Mind: the same modular-merge pattern — fetch the `mind_map` rows for
      // completed attempts and merge newest-per-subsystem. Keyed by subsystem ID
      // (MIND_SUBSYSTEM_IDS), matching `dimension` in migration 0210.
      fetchMindOverview: async (assessmentId: string) => {
        const at = accountGeneration();
        try {
          const completed = get().attemptHistory.filter(
            a => a.assessment_id === assessmentId && a.status === 'completed',
          );
          if (!completed.length) {
            set({ mindOverview: { result: null, perSubsystem: {} }, mindAttemptSummaries: {} });
            return;
          }
          const ids = completed.map(a => a.id);
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', ids)
            .eq('result_type', 'mind_map');
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const byAttempt = new Map<string, MindMap>(
            (data || []).map(r => [r.attempt_id as string, r.result_scores as MindMap]),
          );
          const attempts = completed.map(a => ({
            completedAt: a.completed_at || a.started_at,
            result: byAttempt.get(a.id),
          }));

          const summaries: Record<string, { label: string; band: string; strength: number }> = {};
          for (const a of completed) {
            const payload = byAttempt.get(a.id);
            if (!payload) continue;
            const keys = payload.subsystems.map(s => s.subsystemId);
            summaries[a.id] = {
              label: keys.length === 1 ? keys[0] : 'All subsystems',
              band: payload.overall.overallBand,
              strength: payload.overall.overallStrength,
            };
          }
          set({
            mindOverview: mergeLatestSubsystemResults(attempts, MIND_SUBSYSTEM_IDS),
            mindAttemptSummaries: summaries,
          });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      // Shadow Work overview: the modular model takes each spectrum as its own
      // attempt, so the whole map is the union of each spectrum's LATEST take.
      // Batch-fetch the `shadow_map` rows for completed attempts and merge them.
      fetchShadowOverview: async (assessmentId: string) => {
        const at = accountGeneration();
        try {
          const completed = get().attemptHistory.filter(
            a => a.assessment_id === assessmentId && a.status === 'completed',
          );
          if (!completed.length) {
            set({ shadowOverview: { result: null, perSpectrum: {} }, shadowAttemptSummaries: {} });
            return;
          }
          const ids = completed.map(a => a.id);
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', ids)
            .eq('result_type', 'shadow_map');
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const byAttempt = new Map<string, ShadowMap>(
            (data || []).map(r => [r.attempt_id as string, r.result_scores as ShadowMap]),
          );
          const attempts = completed.map(a => ({
            completedAt: a.completed_at || a.started_at,
            result: byAttempt.get(a.id),
          }));

          // Per-attempt summary for history labels: single-spectrum attempt shows
          // that spectrum's name; a full pass shows "All".
          const summaries: Record<string, { label: string; resultLabel: string; integration: number }> = {};
          for (const a of completed) {
            const payload = byAttempt.get(a.id);
            if (!payload || !payload.spectra.length) continue;
            summaries[a.id] = {
              label: payload.spectra.length === 1 ? payload.spectra[0].name : 'All spectra',
              resultLabel: payload.spectra.length === 1 ? payload.spectra[0].resultLabel : payload.overall.status,
              integration: payload.overall.integration,
            };
          }
          set({ shadowOverview: mergeLatestSpectrumResults(attempts), shadowAttemptSummaries: summaries });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      // The Archetype matrix bridges the user's Shadow Work per-spectrum result.
      // Return the union of each spectrum's LATEST take across all completed
      // Shadow Work attempts (same merge the overview uses). Called at completion
      // (to seed the snapshot) AND at Archetype VIEW time (to recompute live), so
      // the matrix fills in whenever Shadow Work is done, in any order.
      fetchLatestShadowMap: async () => {
        try {
          const shadowAttempts = get().attemptHistory.filter(
            a => a.assessment_id === SHADOW_ID && a.status === 'completed',
          );
          if (!shadowAttempts.length) return null;
          const { data } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', shadowAttempts.map(a => a.id))
            .eq('result_type', 'shadow_map');
          const byAttempt = new Map<string, ShadowMap>(
            (data || []).map(r => [r.attempt_id as string, r.result_scores as ShadowMap]),
          );
          return mergeLatestSpectrumResults(
            shadowAttempts.map(a => ({ completedAt: a.completed_at || a.started_at, result: byAttempt.get(a.id) })),
          ).result;
        } catch {
          return null;
        }
      },

      // Cache the latest completed attempt's results per assessment so the list
      // and profile can show top talents without re-fetching on every render.
      fetchLatestResults: async () => {
        const at = accountGeneration();
        const { attemptHistory } = get();
        const latestAttempts: Record<string, AssessmentAttempt> = {};
        for (const a of attemptHistory) {
          if (a.status !== 'completed') continue;
          const cur = latestAttempts[a.assessment_id];
          if (!cur || new Date(a.completed_at || 0).getTime() > new Date(cur.completed_at || 0).getTime()) {
            latestAttempts[a.assessment_id] = a;
          }
        }
        const entries = await Promise.all(
          Object.entries(latestAttempts).map(async ([assessmentId, attempt]) => {
            const res = await get().fetchAttemptResults(attempt.id);
            return [assessmentId, res] as const;
          })
        );
        if (at !== accountGeneration()) return;
        set({ latestResults: Object.fromEntries(entries) });
      },

      // Rebuild the caches an attempt deletion invalidates: the attempt list, the
      // per-assessment latest-result cache, and the modular whole-picture overview
      // (Life Balance / Energetic / Mind / Shadow) if this assessment has one.
      _refreshAfterHistoryDelete: async (assessmentId: string) => {
        await get().fetchAttemptHistory();
        await get().fetchLatestResults();
        const assessment = get().assessments.find(a => a.id === assessmentId);
        if (assessment?.type === 'life_balance') await get().fetchLifeBalanceOverview(assessmentId);
        else if (assessment?.type === 'energetic') await get().fetchEnergeticOverview(assessmentId);
        else if (assessment?.type === 'mind') await get().fetchMindOverview(assessmentId);
        else if (assessmentId === SHADOW_ID) await get().fetchShadowOverview(assessmentId);
      },

      deleteAssessmentAttempt: async (attemptId: string) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');
          const assessmentId = get().attemptHistory.find(a => a.id === attemptId)?.assessment_id;
          // Cascade (FK ON DELETE CASCADE) removes this attempt's results + responses.
          const { error } = await supabase
            .from('user_assessment_attempts')
            .delete()
            .eq('id', attemptId)
            .eq('user_id', user.id);
          if (error) throw error;
          if (assessmentId) await get()._refreshAfterHistoryDelete(assessmentId);
          return true;
        } catch (error: any) {
          set({ error: error.message });
          return false;
        }
      },

      deleteAssessmentHistory: async (assessmentId: string) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');
          // Delete every attempt for this assessment; cascade clears their results +
          // responses. Earned XP (domain_xp) is a separate ledger and is left intact.
          const { error } = await supabase
            .from('user_assessment_attempts')
            .delete()
            .eq('user_id', user.id)
            .eq('assessment_id', assessmentId);
          if (error) throw error;
          await get()._refreshAfterHistoryDelete(assessmentId);
          return true;
        } catch (error: any) {
          set({ error: error.message });
          return false;
        }
      },

      abandonAssessment: async () => {
        const { currentAttempt } = get();
        if (!currentAttempt) return;

        try {
          await supabase
            .from('user_assessment_attempts')
            .update({ status: 'abandoned' })
            .eq('id', currentAttempt.id);

          set({ currentAttempt: null, responses: {}, currentAssessment: null });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      setCurrentAssessment: (assessment) => {
        set({ currentAssessment: assessment });
      },

      clearCurrentAttempt: () => {
        set({ currentAttempt: null, responses: {}, results: [], currentAssessment: null });
      },

      getCompletedAssessments: () => {
        const { assessments, attemptHistory } = get();
        const completedIds = new Set(
          attemptHistory
            .filter(a => a.status === 'completed')
            .map(a => a.assessment_id)
        );
        return assessments.filter(a => completedIds.has(a.id));
      },

      /**
       * Talent context for the AI Purpose/Vision crafting, from the cached
       * latest talent attempt. Returns empty/null gracefully if they haven't
       * taken/saved one.
       *
       * ⭐⭐ **`mix` IS THE WHOLE POINT, AND IT USED TO BE THROWN AWAY** (owner,
       * 2026-08-13). This read `Object.entries(...).sort(...).map(([name]) =>
       * name)` — it sorted BY the score and then discarded it, so a profile led
       * by one talent at 29.5% reached the model as five bare names,
       * indistinguishable from a five-way tie. The owner's words: *"the app only
       * lists the top talents, which the user can do"*. The SHARE is the signal.
       *
       * ⚠️ `mix` is UNSLICED where `topTalents` keeps its 5. The bottom of the
       * ladder is load-bearing — the generated report reasons about the LOW
       * talents ("Linguistic 2.6% and Digital 1.3% notably low… partner with
       * skilled writers"), and a top-5 slice deletes exactly those rows.
       *
       * ⚠️ `topTalents` KEEPS its old shape and its slice on purpose: it feeds
       * the visible chip row and the "What You're Good At" suggestion list,
       * where five names is the right amount of UI. Widening it would change
       * two surfaces that are not asking for a change.
       *
       * ⚠️ Scores are PERCENTAGE SHARES of the profile (`assessmentStore.test`
       * pins e.g. `{Logical: 50, Creative: 33.3, Musical: 16.7}`), which is why
       * the prompt may label them `%`.
       */
      getTalentContext: () => {
        const rows = get().latestResults[TALENT_ASSESSMENT_ID] || [];
        const dim = rows.find(r => r.result_type === 'dimension_scores');
        const primary = rows.find(r => r.result_type === 'primary_type');
        const mix = dim?.result_scores
          ? Object.entries(dim.result_scores)
              .map(([name, score]) => ({ name, score: Number(score) }))
              .filter(t => Number.isFinite(t.score))
              .sort((a, b) => b.score - a.score)
          : [];
        return {
          mix,
          topTalents: mix.slice(0, 5).map(t => t.name),
          interpretation: primary?.ai_interpretation || null,
          /**
           * ⚠️ May be null for two different reasons, and both are normal: the
           * report predates the brief (2026-08-13), or the deployed ai-proxy is
           * still on the prose prompt. `talentMixBlock` falls back to the raw
           * report in either case, so nothing is lost — it is just coarser.
           */
          brief: coerceTalentBrief(
            rows.find(r => r.result_type === TALENT_BRIEF_RESULT_TYPE)?.result_scores,
          ),
        };
      },

      fetchLifeMapSnapshots: async (attemptIds: string[]) => {
        const at = accountGeneration();
        const have = get().lifeMapSnapshots;
        const missing = attemptIds.filter(id => !(id in have));
        if (!missing.length) return;
        try {
          const { data, error } = await supabase
            .from('user_assessment_results')
            .select('attempt_id, result_scores')
            .in('attempt_id', missing)
            .eq('result_type', LIFE_MAP_RESULT_TYPE);
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const next = { ...get().lifeMapSnapshots };
          for (const row of data || []) next[row.attempt_id as string] = (row.result_scores || {}) as LifeMapSnapshotPayload;
          set({ lifeMapSnapshots: next });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : String(error) });
        }
      },

      getLatestReflectionResult: (assessmentId: string, resultType: string) => {
        // `latestResults` already IS "the latest completed attempt's rows, per
        // assessment" (see fetchLatestResults), so the attempt no longer has to
        // be re-derived here — which is what the quiz-era reader got wrong.
        const rows = get().latestResults[assessmentId] || [];
        return rows.find(r => r.result_type === resultType) || null;
      }
    }),
    {
      name: 'assessment-storage',
      version: 1,
      partialize: (state) => ({
        attemptHistory: state.attemptHistory.slice(0, 50)
      }),
      storage: accountStorage('claim'),
    }
  )
);

scopeToAccount(useAssessmentStore);

/**
 * ⭐⭐ HAS THE USER FINISHED THE TALENT ASSESSMENT — the one definition.
 *
 * Two surfaces ask this and they must never disagree: the Growth Module's step
 * 14 completes on it, and the Direction assessment shows its talent panel on it.
 * Written twice, they drift; written once, a change lands on both.
 *
 * ⚠️⚠️ READS `attemptHistory`, WHICH IS PERSISTED — never `latestResults` and
 * never `getTalentContext()`. `latestResults` is populated only by
 * `fetchLatestResults()`, which just two tabs call, so a surface that gates on
 * it reports "not taken" for a user who took it months ago. That is the same
 * shape as the bug where `getLatestResultForAssessment` could never read a
 * reflection at all: the data was there, the reader was looking in the wrong
 * field. The store's own doc on `getLatestReflectionResult` says exactly this.
 *
 * ⚠️ `status === 'completed'` matters — an abandoned attempt is a row too.
 */
export const selectHasTalentAssessment = (s: AssessmentState): boolean =>
  s.attemptHistory.some(
    a => a.assessment_id === TALENT_ASSESSMENT_ID && a.status === 'completed',
  );
